import { ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProductInterface } from '../../data-access/models/product.interface';
import { ApiImageModule } from '@digipay/ng-ui-api-image';
import { PipesModule } from '@digipay/ng-lib-pipes';
import { NgxSkeletonLoadingComponent } from '@digipay/ngx-skeleton-loading';
import { ProductApiService } from '../../data-access/services/product-api.service';
import { NgxBadgeModule } from '@digipay/ngx-badge';
import { StorePaymentMethod } from '../../data-access/models/store.type';
import { DpIconComponent } from '@client-monorepo/common/icon';
import { toObservable } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { NgxIcon } from '@digipay/ngx-icon';
import { LogoWithRateComponent } from '../logo-with-rate/logo-with-rate.component';
import { PaymentMethodBadgesComponent } from '../payment-method-badges/payment-method-badges.component';

@Component({
  selector: 'daily-stores-product-preview',
  standalone: true,
  imports: [
    CommonModule,
    ApiImageModule,
    PipesModule,
    NgxSkeletonLoadingComponent,
    NgxBadgeModule,
    DpIconComponent,
    NgxIcon,
    LogoWithRateComponent,
    PaymentMethodBadgesComponent,
  ],
  templateUrl: './product-preview.component.html',
  styleUrl: './product-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductPreviewComponent implements OnDestroy {
  classes = input<string>('');
  product = input<ProductInterface>({} as ProductInterface);
  isLoading = input<boolean>(false);
  backgroundMode = input<'elevated' | 'back'>('elevated');
  priceUnit = input<string>('تومان');
  clicked = output<ProductInterface>();
  queryId = input<string | undefined>();
  position = input<string | number | undefined>();
  size = input<'large' | 'small'>('large');

  previousPrice = computed(() => {
    return this.product().discount && this.product().price ? Number(this.product().price) + (this.product().discount ?? 0) : 0;
  });
  offPercent = computed(() => {
    const percent = this.previousPrice() > 0 ? Math.round((100 * (this.product().discount ?? 0)) / this.previousPrice()) : 0;
    return percent > 0 ? `${percent} %` : '';
  });
  titleLimit = computed(() => {
    return this.hasPaymentMethods() ? 30 : 40;
  });

  productImageSrc = signal<string>('');

  productApiService = inject(ProductApiService);
  productContentSub: Subscription = new Subscription();

  constructor() {
    this.productContentSub = toObservable(this.product).subscribe({
      next: (product: ProductInterface) => {
        if (product.image) {
          this.productImageSrc.set(this.product().image);
        } else {
          this.handleImageError();
        }
      },
    });
  }

  handleImageError(): void {
    this.productImageSrc.set(
      this.backgroundMode() === 'elevated'
        ? 'assets/fallback/product-on-elevated-fallback.png'
        : 'assets/fallback/product-on-back-fallback.png',
    );
  }

  hasPaymentMethods = computed(() => {
    return (
      this.product().storePaymentMethods &&
      (this.product().storePaymentMethods?.indexOf(StorePaymentMethod.BNPL) !== -1 ||
        this.product().storePaymentMethods?.indexOf(StorePaymentMethod.C_CREDIT) !== -1)
    );
  });

  paymentMethods = computed(() => {
    const methods = [];
    if (this.hasPaymentMethods()) {
      if (this.product().storePaymentMethods && this.product().storePaymentMethods?.indexOf(StorePaymentMethod.C_CREDIT) !== -1) {
        methods.push({
          name: 'وام',
          icon: 'credit',
        });
      }
      if (this.product().storePaymentMethods && this.product().storePaymentMethods?.indexOf(StorePaymentMethod.BNPL) !== -1) {
        methods.push({
          name: 'اعتبار',
          icon: 'bnpl',
        });
      }
    }
    return methods;
  });

  handleClick(): void {
    if (this.queryId() !== undefined && this.position() !== undefined && this.product().documentId && !this.isLoading()) {
      this.productApiService.clickOnProduct(this.product().documentId, this.queryId() ?? '', this.position() ?? '').subscribe();
    }
    this.clicked.emit(this.product());
  }

  ngOnDestroy(): void {
    if (this.productContentSub) {
      this.productContentSub.unsubscribe();
    }
  }
}
